import { z } from 'zod';
import {
  KLAERFALL_ARTEN,
  RETOUREN_PRUEFERGEBNISSE,
  type CarrierStatus,
  type KlaerfallArt,
  type LabelQuelle,
  type LabelStatus,
  type PaketZielTyp,
  type RetourenCarrier,
  type RetourenGrund,
  type RetourenPositionStatus,
  type RetourenPruefergebnis,
  type RetoureQuelle,
  type RetoureStatus,
} from './retouren';

/**
 * Retouren D4: Händlerportal (Bereich „Retouren“), Händler-API /v1/haendler/retouren, Fristen und automatische Annahme
 * (Briefing Teil D, Abschnitte 7.5 und 8).
 */

/* ------------------------------------------------------------------ Prüfergebnis beim Wareneingang */

export const PRUEFERGEBNIS_CODES = Object.keys(RETOUREN_PRUEFERGEBNISSE) as [RetourenPruefergebnis, ...RetourenPruefergebnis[]];
export const KLAERFALL_ART_CODES = Object.keys(KLAERFALL_ARTEN) as [KlaerfallArt, ...KlaerfallArt[]];

/** Führt das Ergebnis zur Annahme (Erstattung) oder zum Klärfall? „defekt“ nur, wenn der Kunde „defekt“ angegeben hat. */
export function fuehrtZurAnnahme(ergebnis: RetourenPruefergebnis, grund: RetourenGrund): boolean {
  return ergebnis === 'a_ware' || ergebnis === 'b_ware' || (ergebnis === 'defekt' && grund === 'defekt');
}

/** Art des Klärfalls zu einem Prüfergebnis, das nicht zur Annahme führt */
export function klaerfallArtFuer(ergebnis: RetourenPruefergebnis): KlaerfallArt {
  switch (ergebnis) {
    case 'fehlt': return 'leer';
    case 'falscher_artikel': return 'falscher_artikel';
    case 'gebraucht': return 'gebraucht';
    case 'defekt': return 'beschaedigt_kunde';
    default: return 'sonstiges';
  }
}

export const RETOUREN_KLAERFALL_FOTOS_MAX = 5;

export const EingangBuchungSchema = z
  .object({
    positionId: z.string().uuid(),
    ergebnis: z.enum(PRUEFERGEBNIS_CODES),
    notiz: z.string().trim().max(1000).default(''),
    /** Fotos der Prüfung (vorher hochgeladen) – Pflicht beim Klärfall */
    fotos: z.array(z.string().uuid()).max(RETOUREN_KLAERFALL_FOTOS_MAX).default([]),
    /** Abweichende Art des Klärfalls (z. B. Fälschung, Frist überschritten); sonst aus dem Ergebnis */
    klaerfallArt: z.enum(KLAERFALL_ART_CODES).optional(),
    /** Ware zurück in den Bestand – nur ohne automatischen Bestandsabgleich, nur bei A-Ware */
    zurueckInBestand: z.boolean().optional(),
  });
export type EingangBuchung = z.input<typeof EingangBuchungSchema>;

export const EingangSchema = z.object({ buchungen: z.array(EingangBuchungSchema).min(1, 'Bitte mindestens einen Artikel buchen').max(200) });
export type EingangEingabe = z.input<typeof EingangSchema>;

export const AbholungSchema = z.object({ termin: z.string().trim().min(3, 'Bitte den Termin angeben').max(200, 'Höchstens 200 Zeichen') });

export const ScanSchema = z.object({ code: z.string().trim().min(3, 'Bitte einen Code scannen oder eingeben').max(120) });

export const OhneAnmeldungSchema = z.object({
  teilId: z.string().uuid(),
  positionen: z.array(z.object({ positionId: z.string().uuid(), menge: z.number().int().min(1).max(999) })).min(1, 'Bitte mindestens einen Artikel wählen').max(100),
});
export type OhneAnmeldungEingabe = z.input<typeof OhneAnmeldungSchema>;

export const SperrgutSchema = z.object({ sperrgut: z.boolean() });

/** Spalte „sperrgut“ in Importen (Tabelle, Shop, Warenwirtschaft): ja/1/true/x → Sperrgut; fehlt die Spalte → null (unverändert) */
const SPERRGUT_SPALTEN = ['sperrgut', 'bulky', 'bulky_goods', 'oversized', 'sperrig'];
export function sperrgutAusRohdaten(rohdaten: Record<string, string> | null | undefined): boolean | null {
  if (!rohdaten) return null;
  for (const [k, v] of Object.entries(rohdaten)) {
    if (!SPERRGUT_SPALTEN.includes(k.trim().toLowerCase())) continue;
    const w = String(v ?? '').trim().toLowerCase();
    return ['ja', 'j', 'yes', 'y', 'true', 'wahr', '1', 'x'].includes(w);
  }
  return null;
}

/* ------------------------------------------------------------------ Händlerportal */

/** Stand eines Pakets aus Sicht des Händlers – abgeleitet aus seinen Artikeln */
export const HAENDLER_PAKET_STATUS = {
  angemeldet: 'Angemeldet',
  unterwegs: 'Unterwegs',
  zu_buchen: 'Zu buchen',
  klaerfall: 'Klärfall',
  erledigt: 'Erledigt',
  storniert: 'Zurückgezogen',
} as const;
export type HaendlerPaketStatus = keyof typeof HAENDLER_PAKET_STATUS;
export const PORTAL_RETOUREN_FILTER = ['alle', 'angemeldet', 'unterwegs', 'zu_buchen', 'klaerfall', 'erledigt'] as const;
export type PortalRetourenFilter = (typeof PORTAL_RETOUREN_FILTER)[number];

export const PortalRetourenAbfrage = z.object({
  filter: z.enum(PORTAL_RETOUREN_FILTER).default('alle'),
  suche: z.string().trim().max(80).default(''),
  cursor: z.string().max(200).optional(),
  anzahl: z.coerce.number().int().min(1).max(100).default(50),
});

export function haendlerPaketStatus(positionen: readonly RetourenPositionStatus[], storniert: boolean): HaendlerPaketStatus {
  if (storniert) return 'storniert';
  const aktiv = positionen.filter((s) => s !== 'storniert');
  if (!aktiv.length) return 'storniert';
  if (aktiv.includes('klaerfall')) return 'klaerfall';
  if (aktiv.includes('eingegangen')) return 'zu_buchen';
  if (aktiv.includes('unterwegs')) return 'unterwegs';
  if (aktiv.every((s) => s === 'angemeldet')) return 'angemeldet';
  if (aktiv.includes('angemeldet')) return 'unterwegs';
  return 'erledigt';
}

export interface PortalRetoureZeile {
  paketId: string;
  retoureId: string;
  nummer: string;
  paketNr: number;
  bestellNr: string;
  angemeldetAm: string;
  status: HaendlerPaketStatus;
  carrier: RetourenCarrier;
  carrierStatus: CarrierStatus;
  labelStatus: LabelStatus;
  sendungsnummer: string;
  artikel: number;
  /** Buchung bis (danach automatische Annahme) */
  annahmeFristBis: string | null;
  /** weniger als 24 Stunden bis zur Annahme-Frist */
  fristBald: boolean;
  sperrgut: boolean;
  /** Label-Quelle „Händler stellt bereit“ und noch kein Label */
  labelAngefordert: boolean;
}

export interface PortalRetourenSeite {
  zeilen: PortalRetoureZeile[];
  weiter: string | null;
}

export interface PortalRetourenZaehler {
  zuBuchen: number;
  fristBald: number;
  labelAngefordert: number;
  abholung: number;
  klaerfall: number;
}

export interface PortalRetourePosition {
  id: string;
  titel: string;
  sku: string;
  gtin: string;
  grund: RetourenGrund;
  grundText: string;
  status: RetourenPositionStatus;
  pruefergebnis: RetourenPruefergebnis | null;
  pruefNotiz: string;
  geprueftAm: string | null;
  /** Fotos des Kunden und der Prüfung (Download-Adressen im Portal) */
  kundenFotos: string[];
  pruefFotos: string[];
  zurueckInBestand: boolean;
  fristVersaeumt: boolean;
  klaerfall: { art: KlaerfallArt; beschreibung: string; entschieden: boolean } | null;
}

export interface PortalRetoureDetail {
  paketId: string;
  retoureId: string;
  nummer: string;
  paketNr: number;
  pakete: number;
  quelle: RetoureQuelle;
  retoureStatus: RetoureStatus;
  bestellNr: string;
  angemeldetAm: string;
  /** Kunde: Name und Ort der Lieferanschrift (für die Zuordnung im Lager) */
  kunde: { name: string; ort: string };
  status: HaendlerPaketStatus;
  zielTyp: PaketZielTyp;
  carrier: RetourenCarrier;
  carrierStatus: CarrierStatus;
  labelQuelle: LabelQuelle;
  labelStatus: LabelStatus;
  labelFehler: string;
  labelFristBis: string | null;
  sendungsnummer: string;
  abgegebenAm: string | null;
  zugestelltAm: string | null;
  annahmeFristBis: string | null;
  klaerfallFristBis: string | null;
  /** Klärfall jetzt noch möglich? */
  klaerfallMoeglich: boolean;
  sperrgut: boolean;
  abholungTermin: string;
  dateien: { label: string | null; qrCode: string | null; ruecksendeschein: string | null };
  /** Bestand wird automatisch abgeglichen (Shop/Warenwirtschaft) – dann kein „zurück in den Bestand“ */
  bestandAutomatisch: boolean;
  positionen: PortalRetourePosition[];
  verlauf: { am: string; art: string; von: string; text: string }[];
}

export type ScanErgebnis =
  | { art: 'paket'; paketId: string; retoureId: string }
  | { art: 'teilbestellung'; teilId: string; bestellNr: string }
  | { art: 'keine' };

export interface OhneAnmeldungAnsicht {
  teilId: string;
  bestellNr: string;
  positionen: { id: string; titel: string; sku: string; menge: number; frei: number }[];
}

/* ------------------------------------------------------------------ Händler-API */

export const HaendlerApiAbfrage = z.object({
  status: z.enum(['angemeldet', 'unterwegs', 'zu_buchen', 'klaerfall', 'erledigt']).optional(),
  /** nur Retouren, die sich seit diesem Zeitpunkt geändert haben (ISO 8601) */
  seit: z.string().datetime({ offset: true }).optional(),
  cursor: z.string().max(200).optional(),
  anzahl: z.coerce.number().int().min(1).max(200).default(100),
});

const Base64Pdf = z.string().max(7_000_000, 'Höchstens 5 MB');
export const HaendlerApiLabelSchema = z.object({
  paketId: z.string().uuid().optional(),
  sendungsnummer: z.string().trim().min(6).max(40),
  pdfBase64: Base64Pdf,
});

const ApiFoto = z.object({ base64: z.string().max(14_000_000, 'Höchstens 10 MB je Foto') });
export const HaendlerApiEingangSchema = z.object({
  positionen: z.array(z.object({
    id: z.string().uuid(),
    ergebnis: z.enum(PRUEFERGEBNIS_CODES),
    notiz: z.string().trim().max(1000).default(''),
    zurueckInBestand: z.boolean().optional(),
    fotos: z.array(ApiFoto).max(RETOUREN_KLAERFALL_FOTOS_MAX).default([]),
  })).min(1).max(200),
});
export const HaendlerApiKlaerfallSchema = z.object({
  positionId: z.string().uuid(),
  art: z.enum(KLAERFALL_ART_CODES),
  beschreibung: z.string().trim().min(3).max(1000),
  fotos: z.array(ApiFoto).min(1, 'Mindestens ein Foto').max(RETOUREN_KLAERFALL_FOTOS_MAX),
});

export interface HaendlerApiRetoure {
  id: string;
  nummer: string;
  bestellnummer: string;
  angemeldet_am: string;
  geaendert_am: string;
  quelle: RetoureQuelle;
  pakete: {
    id: string;
    nr: number;
    status: HaendlerPaketStatus;
    carrier: RetourenCarrier;
    carrier_status: CarrierStatus;
    sendungsnummer: string;
    label_quelle: LabelQuelle;
    label_status: LabelStatus;
    sperrgut: boolean;
    zugestellt_am: string | null;
    annahme_frist_bis: string | null;
    klaerfall_frist_bis: string | null;
    positionen: {
      id: string;
      sku: string;
      gtin: string;
      titel: string;
      grund: RetourenGrund;
      grund_text: string;
      status: RetourenPositionStatus;
      pruefergebnis: RetourenPruefergebnis | null;
    }[];
  }[];
}
